'use strict'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Formik } from 'formik'
import { throttle } from 'lodash'

import Icon from '@mdi/react'
import { mdiPaperclip } from '@mdi/js'

import Box from '@components/kit/box'
import Button from '@components/kit/button'
import { SendIcon } from '@components/kit/icons'
import { TextInput } from '@components/kit/form/inputs'
import Pressable from '@components/kit/pressable'
import Loading from '@components/kit/loading'
import { ENV } from '@lib/env'
import { colors } from '@theme'
import FileAttachmentPreview from './file-attachment-preview'

const FILE_ATTACHMENT_ENABLED = false
const TYPING_TIMEOUT = 4000
const TEXT_INPUT_MAX_HEIGHT = 152

interface RoomInputProps {
  initialText?: string
  onSendText: (text: string) => void
  onSendTyping: (value: 'start' | 'stop') => void
  onSendFile: (file: File) => Promise<boolean>
}

type Values = {
  text: string
}

function RoomInput({
  initialText = '',
  onSendText,
  onSendTyping,
  onSendFile
}: RoomInputProps) {
  const roomFieldInput = useRef<HTMLInputElement>()
  const typingTimeout = useRef<ReturnType<typeof setTimeout>>()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [isSendingFile, setIsSendingFile] = useState(false)

  // a handed-over draft gets the caret at its end, ready to add to
  useEffect(() => {
    if (initialText) {
      roomFieldInput.current?.setSelectionRange(
        initialText.length,
        initialText.length
      )
    }
  }, [])

  const callStartTyping = useCallback(
    throttle(() => onSendTyping('start'), 60000, { trailing: false }),
    [onSendTyping]
  )

  const callStopTyping = useCallback(() => {
    callStartTyping.cancel()
    onSendTyping('stop')
  }, [callStartTyping, onSendTyping])

  const handleType = () => {
    callStartTyping()
    clearTimeout(typingTimeout.current)
    typingTimeout.current = setTimeout(callStopTyping, TYPING_TIMEOUT)
  }

  const handleSubmit = async (
    values: Values,
    { setFieldValue }: { setFieldValue: (field: string, value: string) => void }
  ) => {
    if (pendingFile) {
      setIsSendingFile(true)
      const success = await onSendFile(pendingFile)
      setIsSendingFile(false)
      if (success) {
        setPendingFile(null)
      }
      return
    }

    if (!values.text.trim()) {
      return
    }

    onSendText(values.text)
    clearTimeout(typingTimeout.current)
    callStartTyping.cancel()
    setFieldValue('text', '')
    roomFieldInput.current?.focus()
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    setPendingFile(file)
    event.target.value = ''
  }

  const quickReplies = ['Thank you', 'Hi', 'Ok', '👍']

  return (
    <Formik
      initialValues={{ text: initialText }}
      onSubmit={handleSubmit}
      validateOnChange={false}
    >
      {({ values, handleChange, handleSubmit: submitForm }) => {
        const canSend = !!pendingFile || !!values.text.trim()
        const isDisabled = isSendingFile

        return (
          <Box flexDirection='column' bg='white'>
            {!pendingFile && values.text.length <= 0 && (
              <Box
                px='16'
                pt='08'
                pb='00'
                justifyContent='flex-start'
                alignItems='center'
                flexDirection='row'
                overflowX='auto'
              >
                {quickReplies.map((response, index) => (
                  <Button
                    key={response}
                    ml={index > 0 ? '12' : '00'}
                    py='12'
                    px='16'
                    borderWidth={1}
                    borderColor='violet'
                    variant='punchThroughViolet'
                    size='small'
                    onPress={() => onSendText(response)}
                    style={{ minWidth: 32 }}
                  >
                    {response}
                  </Button>
                ))}
              </Box>
            )}
            <Box flexDirection='row' p='16' alignItems='center'>
              {FILE_ATTACHMENT_ENABLED && ENV['client_type'] === 'web' && (
                <>
                  <input
                    ref={fileInputRef}
                    type='file'
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                    disabled={isDisabled}
                  />
                  <Pressable
                    mr='08'
                    onPress={() => !isDisabled && fileInputRef.current?.click()}
                    alignItems='center'
                    justifyContent='center'
                    bg='shade02'
                    borderRadius='xlarge'
                    style={{
                      width: 44,
                      height: 44,
                      opacity: isDisabled ? 0.5 : 1
                    }}
                  >
                    <Icon
                      path={mdiPaperclip}
                      size='20px'
                      color={
                        (colors as Record<string, string>).shade05 ?? '#6B7280'
                      }
                    />
                  </Pressable>
                </>
              )}
              {FILE_ATTACHMENT_ENABLED && pendingFile ? (
                <FileAttachmentPreview
                  file_name={pendingFile.name}
                  file_size={pendingFile.size}
                  mime_type={pendingFile.type}
                  variant='composer'
                  onRemove={isDisabled ? undefined : () => setPendingFile(null)}
                />
              ) : (
                <TextInput
                  autoFocus={!!initialText}
                  multiline
                  ref={roomFieldInput}
                  flex='1 0 auto'
                  py='12'
                  px='16'
                  maxHeight={TEXT_INPUT_MAX_HEIGHT}
                  bg='shade02'
                  fontSize={16}
                  textColor='shade07'
                  borderRadius='xlarge'
                  placeholder='Type message...'
                  placeholderTextColor='shade05'
                  value={values.text}
                  onChangeText={text => {
                    handleChange('text')(text)
                    handleType()
                  }}
                  onKeyPress={(event: any) => {
                    if (
                      ENV['client_type'] === 'web' &&
                      event.nativeEvent.key === 'Enter' &&
                      !event.nativeEvent.shiftKey
                    ) {
                      submitForm()
                      event.preventDefault()
                    }
                  }}
                  maxWidth={document.documentElement.clientWidth - 120}
                />
              )}
              <Pressable
                ml='08'
                onPress={() => canSend && !isDisabled && submitForm()}
                alignItems='center'
                justifyContent='center'
                style={
                  {
                    width: 24,
                    height: 24,
                    opacity: canSend && !isDisabled ? 1 : 0.35,
                    cursor: canSend && !isDisabled ? 'pointer' : 'default'
                  } as any
                }
              >
                {isSendingFile ? (
                  <Loading size='small' fill='violet' />
                ) : (
                  <SendIcon width={24} height={24} fill='violet' />
                )}
              </Pressable>
            </Box>
          </Box>
        )
      }}
    </Formik>
  )
}

export default RoomInput
